import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/ja"; // 日本語ロケールをインポート
import "dayjs/locale/en"; // 英語ロケールもインポート
import type { useTranslation } from "react-i18next";
import { PAGE_PATHS } from "@/config/urls";
import {
  CALENDAR_GRID,
  EVENT_LOCATION_TYPE_HYBRID,
  EVENT_LOCATION_TYPE_OFFLINE,
  EVENT_LOCATION_TYPE_ONLINE,
  LANG_EN,
  LANG_JP,
  UNIT_DAY,
  UNIT_HOUR,
  UNIT_MINUTE,
  UNIT_WEEK,
} from "@/constants";
import type { EventApiResponse } from "@/lib/humhub-api/types";
import i18n from "@/lib/i18n";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

type ReminderLike = {
  unit: number;
  value: number;
};

export type CalendarEventParticipationStatus =
  | "attending"
  | "interested"
  | "declined"
  | "invited"
  | "none";

type EventUserFlags = {
  isOwner: boolean;
  isAttending: boolean;
  isInterested: boolean;
  isDeclined: boolean;
  isInvited: boolean;
  participationStatus: CalendarEventParticipationStatus;
  isAttendingOrOwner: boolean;
};

const participationStateStatusMap: Record<
  number,
  CalendarEventParticipationStatus
> = {
  0: "none",
  1: "declined",
  2: "interested",
  3: "attending",
  4: "invited",
};

const participantListIncludesUser = (
  participants: EventApiResponse["participants"][keyof EventApiResponse["participants"]],
  userId?: string | number | null,
) =>
  userId != null &&
  Boolean(
    participants?.some(
      (participant) => String(participant.id) === String(userId),
    ),
  );

export const getEventUserFlags = (
  event: EventApiResponse,
  userId?: string | number | null,
): EventUserFlags => {
  const isOwner = Boolean(event?.is_owner);
  const stateParticipationStatus =
    typeof event.user_participation?.state === "number"
      ? participationStateStatusMap[event.user_participation.state]
      : undefined;
  if (stateParticipationStatus && stateParticipationStatus !== "none") {
    return {
      isOwner,
      isAttending: stateParticipationStatus === "attending",
      isInterested: stateParticipationStatus === "interested",
      isDeclined: stateParticipationStatus === "declined",
      isInvited: stateParticipationStatus === "invited",
      participationStatus: stateParticipationStatus,
      isAttendingOrOwner: isOwner || stateParticipationStatus === "attending",
    };
  }
  if (stateParticipationStatus === "none") {
    const isInvited =
      event.user_participation?.status === "invited" ||
      event.user_participation?.is_invited === true;
    const participationStatus: CalendarEventParticipationStatus = isInvited
      ? "invited"
      : "none";
    return {
      isOwner,
      isAttending: false,
      isInterested: false,
      isDeclined: false,
      isInvited,
      participationStatus,
      isAttendingOrOwner: isOwner,
    };
  }

  const isAttending =
    event.user_participation?.attendance_status === "going" ||
    event.user_participation?.status === "accepted" ||
    participantListIncludesUser(event?.participants?.attending, userId);
  const isInterested =
    event.user_participation?.attendance_status === "interested" ||
    event.user_participation?.status === "maybe" ||
    participantListIncludesUser(event?.participants?.interested, userId) ||
    participantListIncludesUser(event?.participants?.maybe, userId);
  const isDeclined =
    event.user_participation?.attendance_status === "refused" ||
    event.user_participation?.status === "declined" ||
    participantListIncludesUser(event?.participants?.declined, userId);
  const isInvited =
    Boolean(event.user_participation?.is_invited) ||
    event.user_participation?.status === "invited" ||
    participantListIncludesUser(event?.participants?.invited, userId);
  const participationStatus: CalendarEventParticipationStatus = isAttending
    ? "attending"
    : isInterested
      ? "interested"
      : isDeclined
        ? "declined"
        : isInvited
          ? "invited"
          : "none";

  return {
    isOwner,
    isAttending,
    isInterested,
    isDeclined,
    isInvited,
    participationStatus,
    isAttendingOrOwner: isOwner || isAttending,
  };
};

const REMINDER_UNIT_BASE_MAP: Record<number, string> = {
  [UNIT_MINUTE]: "common.minute",
  [UNIT_HOUR]: "common.hour",
  [UNIT_DAY]: "common.day",
  [UNIT_WEEK]: "common.week",
};

export const formatReminderText = (
  reminder: ReminderLike,
  t: ReturnType<typeof useTranslation>["t"],
): string => {
  const unitBase = REMINDER_UNIT_BASE_MAP[reminder.unit] ?? "common.unit";
  const suffix = reminder.value === 1 ? "one" : "other";
  const unitText = t(`${unitBase}_${suffix}`, { count: reminder.value });
  const baseText = `${reminder.value} ${unitText}`;

  return `${baseText}`;
};

interface EventLocationParams {
  locationType?: number | null;
  location?: string | null;
  onlineLocation?: string | null;
  legacyLocation?: string | null;
}

export interface EventLocationDisplay {
  type: string;
  details: string;
}

export const formatEventLocationDisplay = (
  params: EventLocationParams,
  t: ReturnType<typeof useTranslation>["t"],
): EventLocationDisplay => {
  const { locationType, location, onlineLocation, legacyLocation } = params;
  const fallbackLocation = legacyLocation ?? location ?? "";
  const notSetText = t("common.notSet");

  if (locationType === EVENT_LOCATION_TYPE_ONLINE) {
    return {
      type: t("common.online"),
      details: onlineLocation || notSetText,
    };
  }

  if (locationType === EVENT_LOCATION_TYPE_HYBRID) {
    const physicalLocation = location || notSetText;
    const onlineLocationText = onlineLocation || notSetText;
    return {
      type: t("common.hybrid"),
      details: `${t("eventCreate.physicalLocation")}: ${physicalLocation}\n${t("common.eventUrl")}: ${onlineLocationText}`,
    };
  }

  if (locationType === EVENT_LOCATION_TYPE_OFFLINE) {
    return {
      type: t("common.inPerson"),
      details: location || notSetText,
    };
  }

  if (fallbackLocation) {
    if (fallbackLocation === "Online") {
      return {
        type: t("common.online"),
        details: onlineLocation || fallbackLocation,
      };
    }

    if (fallbackLocation.startsWith("Online + ")) {
      return {
        type: t("common.hybrid"),
        details: fallbackLocation,
      };
    }

    return {
      type: t("common.inPerson"),
      details: fallbackLocation,
    };
  }

  return {
    type: notSetText,
    details: notSetText,
  };
};

/**
 * 英語の曜日を日本語に変換
 * @param weekday 英語の曜日（MON, TUE, WED, THU, FRI, SAT, SUN）
 * @returns 日本語の曜日（月, 火, 水, 木, 金, 土, 日）
 */
export const convertWeekdayToJapanese = (weekday: string): string => {
  const weekdayMap: Record<string, string> = {
    MON: "月",
    TUE: "火",
    WED: "水",
    THU: "木",
    FRI: "金",
    SAT: "土",
    SUN: "日",
  };

  return weekdayMap[weekday.toUpperCase()] || weekday;
};

/**
 * 日時文字列を比較用のDateオブジェクトに変換
 */
export const createDateTimeForComparison = (
  dateStr: string,
  timeStr: string,
): Date | null => {
  if (!dateStr || !timeStr) return null;

  try {
    const combinedStr = `${dateStr}T${timeStr}:00`;
    const date = new Date(combinedStr);

    // 無効な日付の場合はnullを返す
    if (Number.isNaN(date.getTime())) return null;

    return date;
  } catch (error) {
    console.warn("Error creating date for comparison:", error);
    return null;
  }
};

/**
 * 開始日時と終了日時の順序を検証
 * @param startDate 開始日 (YYYY-MM-DD)
 * @param startTime 開始時刻 (HH:MM)
 * @param endDate 終了日 (YYYY-MM-DD)
 * @param endTime 終了時刻 (HH:MM)
 * @returns バリデーション結果
 */
export const validateDateTimeOrder = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): { isValid: boolean; errorType?: "start_after_end" | "same_datetime" } => {
  const startDateTime = createDateTimeForComparison(startDate, startTime);
  const endDateTime = createDateTimeForComparison(endDate, endTime);

  // どちらかが無効な場合は検証をスキップ
  if (!startDateTime || !endDateTime) {
    return { isValid: true };
  }

  const startTime_ms = startDateTime.getTime();
  const endTime_ms = endDateTime.getTime();

  if (startTime_ms > endTime_ms) {
    return { isValid: false, errorType: "start_after_end" };
  }

  if (startTime_ms === endTime_ms) {
    return { isValid: false, errorType: "same_datetime" };
  }

  return { isValid: true };
};

/**
 * 終了日時を開始日時の後に自動調整
 * @param startDate 開始日
 * @param startTime 開始時刻
 * @param defaultDuration デフォルトの継続時間（時間）
 * @returns 調整された終了日時
 */
export const autoAdjustEndDateTime = (
  startDate: string,
  startTime: string,
  defaultDuration = 1,
): { endDate: string; endTime: string } | null => {
  const startDateTime = createDateTimeForComparison(startDate, startTime);

  if (!startDateTime) return null;

  const adjustedEndDateTime = new Date(startDateTime);
  adjustedEndDateTime.setHours(
    adjustedEndDateTime.getHours() + defaultDuration,
  );

  const endDate = adjustedEndDateTime.toISOString().split("T")[0];
  const endTime = adjustedEndDateTime.toTimeString().slice(0, 5);

  return { endDate, endTime };
};

/**
 * 日時バリデーションエラーメッセージを取得
 */
export const getDateTimeValidationMessage = (
  errorType: "start_after_end" | "same_datetime",
  t: ReturnType<typeof useTranslation>["t"],
): string => {
  switch (errorType) {
    case "start_after_end":
      return t("common.startAfterEndError");
    case "same_datetime":
      return t("common.sameDateTimeError");
    default:
      return t("common.dateTimeError");
  }
};

/**
 * イベントからサムネイル用のファイルGUIDを取得
 */
export const getEventThumbnailGuid = (
  event: EventApiResponse,
): string | null => {
  return (
    event?.content?.files?.find((file) => file.category === 1)?.guid || null
  );
};

/**
 * イベントの外部公開URLを生成
 */
export const generateEventPublicUrl = (eventCode: string): string => {
  const currentUrl = window.location.origin;
  return `${currentUrl}${PAGE_PATHS.PUBLIC_EVENT}/${eventCode}`;
};

/**
 * イベントリンクをクリップボードにコピー
 */
export const copyEventLinkToClipboard = async (
  eventCode: string,
  t: ReturnType<typeof import("react-i18next").useTranslation>["t"],
): Promise<void> => {
  try {
    const eventLink = generateEventPublicUrl(eventCode);
    await navigator.clipboard.writeText(eventLink);

    // toastは呼び出し元で処理する必要があるため、成功時は何もしない
  } catch (error) {
    console.error("Failed to copy event link:", error);
    throw new Error(
      t("eventManage.linkCopyFailed", "Failed to copy event link"),
    );
  }
};

/**
 * eventからdate, month, time, weekday(optional)を抽出
 */
export const parseEventDate = (
  event: EventApiResponse,
  locale = LANG_EN,
): {
  startDate: string;
  startMonth: string;
  endDate: string;
  endMonth: string;
  startTime: string;
  endTime: string;
  startWeekday: string;
  endWeekday: string;
} => {
  try {
    const isJapanese = locale === LANG_JP;
    dayjs.locale(isJapanese ? LANG_JP : LANG_EN);

    const startTime = dayjs.tz(event.start_datetime, event.time_zone);
    const localStartTime = startTime.local();
    const endTime = event.end_datetime
      ? dayjs.tz(event.end_datetime, event.time_zone)
      : null;
    const localEndTime = endTime ? endTime.local() : null;

    // 日付部分
    let startMonth: string;
    let startDate: string;
    let endMonth: string;
    let endDate: string;
    if (isJapanese) {
      startMonth = String(localStartTime.month() + 1);
      startDate = String(localStartTime.date());
      endMonth = localEndTime ? String(localEndTime.month() + 1) : "";
      endDate = localEndTime ? String(localEndTime.date()) : "";
    } else {
      startDate = localStartTime.format("D");
      startMonth = localStartTime.format("MMM");
      endDate = localEndTime ? localEndTime.format("D") : "";
      endMonth = localEndTime ? localEndTime.format("MMM") : "";
    }

    // 時刻部分（24時間表記）
    const startTimeDisplay =
      event.all_day === 1 ? "" : localStartTime.format("HH:mm");
    const endTimeDisplay =
      event.all_day === 1 || !localEndTime ? "" : localEndTime.format("HH:mm");

    // 曜日
    const startWeekdayDisplay = localStartTime.format("ddd");
    const endWeekdayDisplay = localEndTime ? localEndTime.format("ddd") : "";

    return {
      startDate,
      startMonth,
      endDate,
      endMonth,
      startTime: startTimeDisplay,
      endTime: endTimeDisplay,
      startWeekday: startWeekdayDisplay,
      endWeekday: endWeekdayDisplay,
    };
  } catch (error) {
    console.warn("Error formatting event card date display:", error);
    return {
      startDate: "0/0",
      startMonth: "0",
      endDate: "0/0",
      endMonth: "0",
      startWeekday: "Err",
      endWeekday: "Err",
      startTime: "00:00",
      endTime: "00:00",
    };
  }
};

/**
 * カレンダー表示用の詳細な日時フォーマット
 * EventDetailContent等で使用
 *
 * Day.jsのl/ll/lll/llllフォーマットを使用し、言語に応じた自動フォーマット
 * - 終日イベント（単一日）: dayjs.format("lll") で言語別自動フォーマット
 * - 終日イベント（複数日）: "lll - ll" 形式
 * - 通常イベント（単一日）: dayjs.format("llll") で言語別自動フォーマット
 * - 通常イベント（複数日）: "llll -\nllll" 形式
 */
export const formatEventDetailDate = (event: EventApiResponse): string => {
  try {
    // i18nから直接現在の言語を取得してdayjs locale設定
    const currentLanguage = i18n.language;
    dayjs.locale(currentLanguage);

    // タイムゾーン変換
    const startDateTime = dayjs
      .tz(event.start_datetime, event.time_zone)
      .local();
    const endDateTime = event.end_datetime
      ? dayjs.tz(event.end_datetime, event.time_zone).local()
      : null;

    // イベントが同じ日に収まるかどうかを判定
    const isSameDay = !endDateTime || startDateTime.isSame(endDateTime, "day");

    // 終日イベントの場合
    if (event.all_day === 1) {
      return formatAllDayEvent(startDateTime, endDateTime, isSameDay);
    }

    // 通常イベント（時間指定）の場合
    return formatTimedEvent(startDateTime, endDateTime, isSameDay);
  } catch (error) {
    console.warn("Error formatting event detail date:", error);
    return event.start_datetime;
  }
};

/**
 * イベントを日付別にグループ化
 */
export const groupEventsByDay = (
  events: EventApiResponse[],
  currentDate: Date,
): Map<number, EventApiResponse[]> => {
  const eventsByDay = new Map<number, EventApiResponse[]>();

  events.forEach((event) => {
    try {
      const eventTime = dayjs.tz(event.start_datetime, event.time_zone);
      const localEventTime = eventTime.local();
      const eventStartDate = localEventTime.toDate();

      let localEndTime = event.end_datetime
        ? dayjs.tz(event.end_datetime, event.time_zone).local()
        : null;
      if (event.all_day === 1 && localEndTime) {
        // 終日イベントの場合、終了日を1日前に調整する
        localEndTime = localEndTime.subtract(1, "day");
      }
      const eventEndDate = localEndTime?.toDate() ?? null;

      // イベントの期間内のすべての日にイベントを追加
      const daysToAdd: number[] = [];
      if (eventEndDate) {
        // 終了日時がある場合、開始日から終了日までのすべての日を対象とする
        const startDate = new Date(eventStartDate);
        const endDate = new Date(eventEndDate);

        // 日付を日単位で比較するために時間を00:00:00に正規化
        const normalizedStart = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
        );
        const normalizedEnd = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
        );

        // 開始日から終了日までのすべての日を追加
        const currentDay = new Date(normalizedStart);
        while (currentDay <= normalizedEnd) {
          if (isEventInCurrentMonth(currentDay, currentDate)) {
            daysToAdd.push(currentDay.getDate());
          }
          currentDay.setDate(currentDay.getDate() + 1);
        }
      } else {
        // 終了日時がない場合は開始日のみ
        if (isEventInCurrentMonth(eventStartDate, currentDate)) {
          daysToAdd.push(eventStartDate.getDate());
        }
      }

      // 各対象日にイベントを追加
      daysToAdd.forEach((day) => {
        if (!eventsByDay.has(day)) {
          eventsByDay.set(day, []);
        }
        eventsByDay.get(day)?.push(event);
      });
    } catch (error) {
      console.warn("Error processing event for grouping:", error, event);
    }
  });

  return eventsByDay;
};

/**
 * 指定した日のイベントを取得
 */
export const getDayEvents = (
  eventsByDay: Map<number, EventApiResponse[]>,
  day: number,
): EventApiResponse[] => {
  return eventsByDay.get(day) || [];
};

/**
 * 時間文字列から時間の数値を抽出
 */
export const getEventHour = (timeStr: string): number | null => {
  if (!timeStr || !timeStr.includes(":")) return null;
  const hour = Number.parseInt(timeStr.split(":")[0], 10);
  return Number.isNaN(hour) ? null : hour;
};

/**
 * 月の最初の日を取得（月曜日開始）
 */
export const getFirstDayOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * 月の最後の日を取得
 */
export const getLastDayOfMonth = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * 曜日のオフセットを計算（月曜日を0とする）
 */
export const getMondayBasedDayOffset = (date: Date): number => {
  return (date.getDay() + 6) % 7; // Monday = 0
};

/**
 * カレンダーの週単位の配列を生成
 */
export const generateCalendarWeeks = (
  currentDate: Date,
): (number | null)[][] => {
  const firstDay = getFirstDayOfMonth(currentDate);
  const lastDay = getLastDayOfMonth(currentDate);
  const firstDayOffset = getMondayBasedDayOffset(firstDay);
  const daysInMonth = lastDay.getDate();

  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = [];

  // 月の最初の週の空セルを追加
  for (let i = 0; i < firstDayOffset; i++) {
    week.push(null);
  }

  // 月の日付を追加
  for (let i = 1; i <= daysInMonth; i++) {
    week.push(i);
    if (week.length === CALENDAR_GRID.DAYS_PER_WEEK) {
      weeks.push([...week]);
      week = [];
    }
  }

  // 最後の週の空セルを追加
  if (week.length > 0) {
    while (week.length < CALENDAR_GRID.DAYS_PER_WEEK) {
      week.push(null);
    }
    weeks.push([...week]);
  }

  return weeks;
};

/**
 * 日付文字列を正規化（スラッシュをハイフンに変換）
 */
export const normalizeDateString = (dateStr: string): string => {
  return dateStr.replace(/\//g, "-");
};

/**
 * 指定した月の日付かどうかを判定
 */
export const isEventInCurrentMonth = (
  eventDate: Date,
  currentDate: Date,
): boolean => {
  return (
    eventDate.getMonth() === currentDate.getMonth() &&
    eventDate.getFullYear() === currentDate.getFullYear()
  );
};

/**
 * カレンダー表示期間を計算（週単位）
 */
export const getCalendarViewPeriod = (
  currentDate: Date,
): { startDate: string; endDate: string } => {
  const firstDay = getFirstDayOfMonth(currentDate);
  const lastDay = getLastDayOfMonth(currentDate);

  // 月の最初の週の月曜日
  const firstDayOffset = getMondayBasedDayOffset(firstDay);
  const viewStartDate = new Date(firstDay);
  viewStartDate.setDate(firstDay.getDate() - firstDayOffset);

  // 月の最後の週の日曜日
  const lastDayOffset = getMondayBasedDayOffset(lastDay);
  const viewEndDate = new Date(lastDay);
  viewEndDate.setDate(lastDay.getDate() + (6 - lastDayOffset));

  return {
    startDate: dayjs(viewStartDate).format("YYYY-MM-DD"),
    endDate: dayjs(viewEndDate).format("YYYY-MM-DD"),
  };
};

// Serializer は分離: src/lib/utils/event/api-serializer
export {
  transformFormToCreateApiRequest,
  transformFormToDraftApiRequest,
  transformFormToUpdateApiRequest,
} from "@/lib/utils/event/api-serializer";

// ========================================
// スタイル定数
// ========================================

export const CALENDAR_CELL_STYLES = {
  base: "bg-white px-2 pt-1 pb-1.5 flex flex-col",
  hover: "hover:bg-gray-50",
  empty: "bg-gray-50",
  today: "bg-blue-50 ring-2 ring-inset ring-blue-200",
} as const;

export const GRID_STYLES = {
  calendar:
    "grid h-full grid-cols-7 gap-px overflow-hidden rounded-lg bg-gray-200",
  timeSlot: "grid grid-cols-[auto_1fr] gap-x-4",
} as const;

export const HEADER_STYLES = {
  weekday:
    "flex h-7 items-center justify-center bg-white p-0 text-center text-xs font-medium leading-none",
  timeLabel: "h-20 flex items-center justify-end pr-2 border-b",
} as const;

export const BUTTON_STYLES = {
  primary:
    "px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50",
  navigation: "p-1 hover:bg-gray-100 rounded",
  close: "p-1 rounded-full hover:bg-gray-100 transition-colors",
} as const;

export const getTodayStyle = (isToday: boolean): string => {
  return isToday ? CALENDAR_CELL_STYLES.today : "";
};

export const buildCalendarCellStyle = (
  hasDay: boolean,
  isToday = false,
): string => {
  const baseStyle = CALENDAR_CELL_STYLES.base;
  const hoverStyle = hasDay ? CALENDAR_CELL_STYLES.hover : "";
  const emptyStyle = hasDay ? "" : CALENDAR_CELL_STYLES.empty;
  const todayStyle = getTodayStyle(isToday);

  return `${baseStyle} ${hoverStyle} ${emptyStyle} ${todayStyle}`.trim();
};

export const LOADING_OVERLAY_STYLE =
  "absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50 rounded-lg";

/**
 * 終日イベントの日時フォーマット
 */
const formatAllDayEvent = (
  startDateTime: dayjs.Dayjs,
  endDateTime: dayjs.Dayjs | null,
  isSameDay: boolean,
): string => {
  if (!endDateTime || isSameDay) {
    // 単一日イベントの場合 - lllを使用（言語に応じた自動フォーマット）
    return startDateTime.format("lll");
  }

  // 複数日にわたるイベントの場合
  const startDateStr = startDateTime.format("lll");
  const endDateStr = endDateTime.format("ll");

  return `${startDateStr} - ${endDateStr}`;
};

/**
 * 通常イベント（時間指定）の日時フォーマット
 */
const formatTimedEvent = (
  startDateTime: dayjs.Dayjs,
  endDateTime: dayjs.Dayjs | null,
  isSameDay: boolean,
): string => {
  if (!endDateTime || isSameDay) {
    // 単一日イベントの場合 - llllを使用（言語に応じた日時フォーマット）
    const startFormat = startDateTime.format("llll");

    if (!endDateTime) {
      // 終了時間なし
      return startFormat;
    }

    // 終了時間あり - 同じ日なので時間のみで十分
    const endTimeStr = endDateTime.format("LT"); // 時間のみ
    const startTimeStr = startDateTime.format("LT"); // 開始時間も取得

    return `${startDateTime.format("ll")}\n${startTimeStr} - ${endTimeStr}`;
  }

  // 複数日にわたるイベントの場合
  const startFormat = startDateTime.format("llll");
  const endFormat = endDateTime.format("llll");

  return `${startFormat} -\n${endFormat}`;
};

/**
 * 複数日イベントの表示プロパティを計算
 */
export interface MultiDayEventDisplayProps {
  isMultiDay: boolean;
  isFirstDay: boolean;
  isLastDay: boolean;
}

/**
 * イベントが複数日にわたるかどうかを判定し、表示プロパティを返す
 * @param event 判定対象のイベント
 * @param currentDay カレンダー上の日付（1-31）
 * @param currentDate カレンダーの基準日
 * @returns 複数日イベントの表示プロパティ
 */
export const getMultiDayEventDisplayProps = (
  event: EventApiResponse,
  currentDay: number,
  currentDate: Date,
): MultiDayEventDisplayProps => {
  try {
    if (!event.end_datetime) {
      return { isMultiDay: false, isFirstDay: false, isLastDay: false };
    }

    // タイムゾーンを考慮して日時を変換
    const startTime = dayjs.tz(event.start_datetime, event.time_zone).local();
    let endTime = dayjs.tz(event.end_datetime, event.time_zone).local();
    if (event.all_day === 1) {
      // 終日イベントの場合、終了日を1日前に調整する（groupEventsByDayと同じロジック）
      endTime = endTime.subtract(1, "day");
    }

    // 日付のみで比較するために時間を00:00:00に正規化
    const normalizedStart = new Date(
      startTime.year(),
      startTime.month(),
      startTime.date(),
    );
    const normalizedEnd = new Date(
      endTime.year(),
      endTime.month(),
      endTime.date(),
    );
    const normalizedCurrentDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDay,
    );

    // 同じ日の場合は単一日イベント
    if (normalizedStart.getTime() === normalizedEnd.getTime()) {
      return { isMultiDay: false, isFirstDay: false, isLastDay: false };
    }

    // 複数日イベントの場合、各日の位置を判定
    const isFirstDay =
      normalizedCurrentDay.getTime() === normalizedStart.getTime();
    const isLastDay =
      normalizedCurrentDay.getTime() === normalizedEnd.getTime();

    return { isMultiDay: true, isFirstDay, isLastDay };
  } catch (error) {
    console.warn("Error determining multi-day event display props:", error);
    return { isMultiDay: false, isFirstDay: false, isLastDay: false };
  }
};

/**
 * 複数日イベントのスタイルクラスを生成
 */
export const getMultiDayEventStyle = (
  displayProps: MultiDayEventDisplayProps,
): string => {
  if (!displayProps.isMultiDay) return "";

  if (displayProps.isFirstDay) return "!rounded-r-none !border-r-0";
  if (displayProps.isLastDay) return "!rounded-l-none !border-l-0";

  return "!rounded-none !border-l-0 !border-r-0";
};

/**
 * 複数日イベントのタイトル表示を決定
 */
export const getMultiDayEventTitle = (
  event: EventApiResponse,
  displayProps: MultiDayEventDisplayProps,
  displayTime: string,
): string => {
  // 単一日イベントまたは最初の日の場合は時間を表示
  if (!displayProps.isMultiDay || displayProps.isFirstDay) {
    return `${displayTime} ${event.title}`;
  }

  // 複数日イベントの2日目以降はタイトルのみ表示
  return event.title;
};
